import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Save, X, Camera, Music, Globe, MapPin, Palette, Users, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = "/api";

const ProfileEdit = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableTracks, setAvailableTracks] = useState([]);
  const [form, setForm] = useState({
    display_name: '',
    bio: '',
    profile_pic: '',
    cover_photo: '',
    location: '',
    website: '',
    phone: '',
    instagram: '',
    twitter: '',
    facebook: '',
    youtube: '',
    spotify: '',
    tiktok: '',
    profile_song_track_id: '',
    show_visitor_count: true,
    show_top_friends: true,
    top_friends_ids: [],
    theme_primary_color: '#8B5CF6',
    theme_secondary_color: '#EC4899',
    theme_bg_color: '#0F0F23',
    theme_text_color: '#E5E7EB',
    theme_font: 'default',
  });

  useEffect(() => {
    if (!user || !token) { navigate('/login'); return; }
    fetchMyProfile();
  }, [user, token]);

  const fetchMyProfile = async () => {
    try {
      const res = await axios.get(`${API}/social/profiles/me/edit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const { profile, available_tracks } = res.data;
      if (profile) {
        setForm(prev => ({
          ...prev,
          ...Object.fromEntries(Object.entries(profile).filter(([_, v]) => v != null)),
          display_name: profile.display_name || user.name || '',
        }));
      } else {
        setForm(prev => ({ ...prev, display_name: user.name || '' }));
      }
      setAvailableTracks(available_tracks || []);
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/social/profiles/me`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated!');
      navigate(`/u/${user.id}`);
    } catch (err) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-12 text-center">
        <div className="h-8 w-48 bg-muted animate-pulse rounded mx-auto" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto pb-24" data-testid="profile-edit-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Edit Profile</h1>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="cancel-edit-btn">
              <X size={16} className="mr-1" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">
              <Save size={16} className="mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <Section title="Basic Info" icon={<Users size={16} />}>
            <Field label="Display Name">
              <Input value={form.display_name} onChange={e => update('display_name', e.target.value)} placeholder="Your name" data-testid="edit-display-name" />
            </Field>
            <Field label="Bio">
              <Textarea value={form.bio} onChange={e => update('bio', e.target.value)} placeholder="Tell people about yourself..." rows={4} data-testid="edit-bio" />
            </Field>
            <Field label="Location">
              <Input value={form.location} onChange={e => update('location', e.target.value)} placeholder="Twin Cities, MN" data-testid="edit-location" />
            </Field>
          </Section>

          {/* Images */}
          <Section title="Images" icon={<Camera size={16} />}>
            <Field label="Profile Picture URL">
              <Input value={form.profile_pic} onChange={e => update('profile_pic', e.target.value)} placeholder="https://..." data-testid="edit-profile-pic" />
            </Field>
            {form.profile_pic && (
              <img src={form.profile_pic} alt="Preview" className="w-20 h-20 rounded-full object-cover border border-border" />
            )}
            <Field label="Cover Photo URL">
              <Input value={form.cover_photo} onChange={e => update('cover_photo', e.target.value)} placeholder="https://..." data-testid="edit-cover-photo" />
            </Field>
          </Section>

          {/* Profile Song - Local Musicians Only */}
          <Section title="Profile Song" icon={<Music size={16} />}>
            <p className="text-xs text-gray-500 mb-2">Choose a song from local Twin Cities musicians</p>
            <select
              value={form.profile_song_track_id}
              onChange={e => update('profile_song_track_id', e.target.value)}
              className="w-full bg-card border border-border rounded-lg px-3 py-2 text-sm"
              data-testid="edit-profile-song"
            >
              <option value="">No profile song</option>
              {availableTracks.map(track => (
                <option key={track.id} value={track.id}>
                  {track.title} — {track.artist}
                </option>
              ))}
            </select>
          </Section>

          {/* Social Links */}
          <Section title="Social Links" icon={<Globe size={16} />}>
            <Field label="Website">
              <Input value={form.website} onChange={e => update('website', e.target.value)} placeholder="https://..." data-testid="edit-website" />
            </Field>
            <Field label="Instagram">
              <Input value={form.instagram} onChange={e => update('instagram', e.target.value)} placeholder="@username" data-testid="edit-instagram" />
            </Field>
            <Field label="Twitter / X">
              <Input value={form.twitter} onChange={e => update('twitter', e.target.value)} placeholder="@handle" data-testid="edit-twitter" />
            </Field>
            <Field label="Facebook">
              <Input value={form.facebook} onChange={e => update('facebook', e.target.value)} placeholder="URL" data-testid="edit-facebook" />
            </Field>
            <Field label="YouTube">
              <Input value={form.youtube} onChange={e => update('youtube', e.target.value)} placeholder="Channel URL" data-testid="edit-youtube" />
            </Field>
            <Field label="Spotify">
              <Input value={form.spotify} onChange={e => update('spotify', e.target.value)} placeholder="Artist URL" data-testid="edit-spotify" />
            </Field>
            <Field label="TikTok">
              <Input value={form.tiktok} onChange={e => update('tiktok', e.target.value)} placeholder="@username" data-testid="edit-tiktok" />
            </Field>
          </Section>

          {/* MySpace Features */}
          <Section title="MySpace Features" icon={<Eye size={16} />}>
            <div className="flex items-center justify-between">
              <Label>Show Visitor Counter</Label>
              <Switch checked={form.show_visitor_count} onCheckedChange={v => update('show_visitor_count', v)} data-testid="toggle-visitor-count" />
            </div>
            <div className="flex items-center justify-between">
              <Label>Show Top Friends</Label>
              <Switch checked={form.show_top_friends} onCheckedChange={v => update('show_top_friends', v)} data-testid="toggle-top-friends" />
            </div>
          </Section>

          {/* Theme Customization */}
          <Section title="Theme Colors" icon={<Palette size={16} />}>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Primary Color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.theme_primary_color} onChange={e => update('theme_primary_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" data-testid="edit-primary-color" />
                  <Input value={form.theme_primary_color} onChange={e => update('theme_primary_color', e.target.value)} className="flex-1" />
                </div>
              </Field>
              <Field label="Secondary Color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.theme_secondary_color} onChange={e => update('theme_secondary_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" data-testid="edit-secondary-color" />
                  <Input value={form.theme_secondary_color} onChange={e => update('theme_secondary_color', e.target.value)} className="flex-1" />
                </div>
              </Field>
              <Field label="Background Color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.theme_bg_color} onChange={e => update('theme_bg_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={form.theme_bg_color} onChange={e => update('theme_bg_color', e.target.value)} className="flex-1" />
                </div>
              </Field>
              <Field label="Text Color">
                <div className="flex items-center gap-2">
                  <input type="color" value={form.theme_text_color} onChange={e => update('theme_text_color', e.target.value)} className="w-10 h-10 rounded cursor-pointer" />
                  <Input value={form.theme_text_color} onChange={e => update('theme_text_color', e.target.value)} className="flex-1" />
                </div>
              </Field>
            </div>
            {/* Preview */}
            <div
              className="mt-3 p-4 rounded-lg border border-border"
              style={{ background: form.theme_bg_color }}
            >
              <p className="text-xs text-gray-500 mb-1">Preview</p>
              <p className="font-bold" style={{ color: form.theme_primary_color }}>Primary Text</p>
              <p className="text-sm" style={{ color: form.theme_secondary_color }}>Secondary accent</p>
              <p className="text-sm" style={{ color: form.theme_text_color }}>Regular body text</p>
            </div>
          </Section>
        </div>
      </motion.div>
    </div>
  );
};

const Section = ({ title, icon, children }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4">
      {icon} {title}
    </h2>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-gray-500 mb-1 block">{label}</Label>
    {children}
  </div>
);

export default ProfileEdit;
